import { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { buildChartData, type ChartRawPoint } from '../../../hooks/useDashboardData';

interface Props {
  chartRawData: ChartRawPoint[];
  loading: boolean;
}

const periodOptions: { label: string; months: number }[] = [
  { label: '3 meses', months: 3 },
  { label: '6 meses', months: 6 },
  { label: '12 meses', months: 12 },
];

const formatCurrencyShort = (value: number) =>
  value >= 1000 ? `R$ ${(value / 1000).toFixed(1)}k` : `R$ ${value.toFixed(0)}`;

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-3">
        <p className="text-sm font-600 text-gray-900 mb-2">{label}</p>
        {payload.map((entry: any) => (
          <div key={entry.name} className="flex items-center gap-2 text-xs">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-gray-600">
              {entry.dataKey === 'receitas' ? 'Receitas' : 'Despesas'}:
            </span>
            <span className="font-600 text-gray-900">
              {Number(entry.value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const RevenueChart = ({ chartRawData, loading }: Props) => {
  const [activePeriod, setActivePeriod] = useState(periodOptions[1]);

  const chartData = buildChartData(chartRawData, activePeriod.months);
  const hasData = chartData.some((d) => d.receitas > 0 || d.despesas > 0);

  const lastPoint = chartData[chartData.length - 1];
  const balance = lastPoint ? lastPoint.receitas - lastPoint.despesas : 0;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 animate-pulse">
        <div className="flex justify-between mb-6">
          <div className="space-y-2">
            <div className="h-4 bg-gray-100 rounded w-40" />
            <div className="h-3 bg-gray-100 rounded w-28" />
          </div>
          <div className="h-9 bg-gray-100 rounded-xl w-40" />
        </div>
        <div className="h-[260px] bg-gray-100 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-base font-700 text-gray-900">Receitas x Despesas</h3>
          <p className="text-sm text-gray-500 mt-0.5">Comparativo mensal</p>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          {periodOptions.map((p) => (
            <button
              key={p.label}
              onClick={() => setActivePeriod(p)}
              className={`px-3 py-1.5 text-xs font-600 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                activePeriod.label === p.label
                  ? 'bg-white text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Balance highlight */}
      {hasData && (
        <div className="flex items-center gap-6 mb-6 p-3 bg-gray-50 rounded-xl">
          <div>
            <p className="text-xs text-gray-500">Saldo do mês atual</p>
            <p className={`text-lg font-700 ${balance >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {balance.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-emerald-500 rounded-sm" />
              <span className="text-xs text-gray-500">Receitas</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-red-300 rounded-sm" />
              <span className="text-xs text-gray-500">Despesas</span>
            </div>
          </div>
        </div>
      )}

      {hasData ? (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} barGap={4} barCategoryGap="30%">
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
            <XAxis
              dataKey="mes"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9CA3AF', fontSize: 12, fontFamily: 'Outfit' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9CA3AF', fontSize: 11, fontFamily: 'Outfit' }}
              tickFormatter={formatCurrencyShort}
              width={55}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F9FAFB' }} />
            <Bar dataKey="receitas" fill="#10B981" radius={[6, 6, 0, 0]} maxBarSize={40} />
            <Bar dataKey="despesas" fill="#FCA5A5" radius={[6, 6, 0, 0]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-[260px] flex flex-col items-center justify-center gap-3 text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-50 rounded-2xl">
            <i className="ri-bar-chart-2-line text-2xl text-gray-300" />
          </div>
          <div>
            <p className="text-sm font-600 text-gray-500">Nenhum lançamento ainda</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Registre receitas e despesas para ver o gráfico
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
